import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Entry } from 'contentful';
import { ProfilePage } from 'src/app/model/page/group/user/profile-page';
import { UserAccount } from 'src/app/model/page/group/user/user-account-request';
import { ApiService, Response } from 'src/app/service/api.service';
import { entraAuthService } from 'src/app/service/auth.service';
import { ContentfulService } from 'src/app/service/contentful.service';
import { ConfigService } from 'src/app/service/config.service';
import { SandboxPageStateService } from 'src/app/service/sandbox-page-state.service';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ProfilePageComponent implements OnInit, OnDestroy {
  apiResponse: Response = {} as Response;
  profilePage: ProfilePage = {} as ProfilePage;
  userAccountRequest: UserAccount = {} as UserAccount;
  user: any;

  detailsExpanded = true;
  preferencesExpanded = true;

  emailPreferenceForm: FormGroup = new FormGroup({
    NDCCommercialNews: new FormControl(false),
    IAGPortalUsers: new FormControl(false),
    platformUpdates: new FormControl(false),
    TechnicalOperationalAlerts: new FormControl(false),
    operationalOldAPIUpdates: new FormControl(false),
    operationalNewAPIUpdates: new FormControl(false),
    firstName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(30)]),
    lastName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(30)])
  });
  submitted = false;
  showPreferences = false;
  showSaveSuccessMsg = false;
  registrationError = '';
  userId = '';
  preferred_userName = '';
  bannerWelcomeName = '';

  constructor(
    private contentfulService: ContentfulService,
    public entraAuthService: entraAuthService,
    public apiService: ApiService,
    private sandboxPageStateService: SandboxPageStateService,
    private router: Router,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'user-account') {
        this.profilePage.htmlTitle = title.htmlTitle;
      }
    });
    this.apiResponse = {} as Response;

    const query = { 'fields.title': 'Profile' };

    this.contentfulService.getPageContentResponse(query).then(async (profilePageEntries) => {
      this.mapBanner(profilePageEntries.includes.Entry);
      this.mapHtmlTitle(profilePageEntries.items);
      let userInfo: any = localStorage.getItem('user_info') || localStorage.getItem('user_Info') || '';
      try { 
        this.user = userInfo ? JSON.parse(userInfo) : null; 
      } catch { this.user = null; }
      if (this.isValidUserObject(this.user)) {
        await this.setUserName();
      }
    });
  }

  private isValidUserObject(user: any): boolean {
    return user !== null && typeof user === 'object' && !Array.isArray(user);
  }

  private mapHtmlTitle(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      this.profilePage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  async setUserName() {
    const { userId, preferredUserName } = this.extractUserInfo();
    this.userId = userId;
    this.preferred_userName = preferredUserName;
    localStorage.setItem('preferred_userName', preferredUserName);
    this.getUserInformation(this.userId);
  }

  private extractUserInfo(): { userId: string; preferredUserName: string } {
    return { userId: this.user?.userId, preferredUserName: this.user.preferredUserName };
  }

  ngOnDestroy(): void {
    localStorage.removeItem('redirect_url');
  }

  public mapBanner(profilePageEntries: Entry<any>[]) {
    profilePageEntries &&
      profilePageEntries
        .filter((entry) => ModelValidator.isBanner(entry))
        .forEach((entry) => {
          this.mapEntryToBanner(entry);
        });
  }

  private mapEntryToBanner(entry: Entry<any>) {
    let imageDetails: any = entry.fields['image'];
    let videoDetails: any = entry.fields['video'];
    this.profilePage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: imageDetails.fields.file.url as string,
      video: videoDetails?.fields?.file?.url as string
    };
  }

  onEmailKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') return;
    event.preventDefault();
    event.stopPropagation();
  }

  onTextInput(event: any, controlName: string): void {
    const value = event?.detail?.value ?? '';
    this.emailPreferenceForm.get(controlName)?.setValue(value);
  }

  onCheckboxChange(event: any, controlName: string): void {
    const isChecked = event?.detail?.checked ?? false;
    this.emailPreferenceForm.get(controlName)?.setValue(isChecked);

    if (controlName === 'TechnicalOperationalAlerts') {
      this.syncSubAlerts(isChecked);
    } else if (controlName === 'operationalOldAPIUpdates' || controlName === 'operationalNewAPIUpdates') {
      this.syncParentAlert();
    }
  }

  private syncSubAlerts(isChecked: boolean): void {
    this.emailPreferenceForm.get('operationalOldAPIUpdates')?.setValue(isChecked);
    this.emailPreferenceForm.get('operationalNewAPIUpdates')?.setValue(isChecked);
  }

  private syncParentAlert(): void {
    const isChecked = this.emailPreferenceForm.get('operationalOldAPIUpdates')?.value
      && this.emailPreferenceForm.get('operationalNewAPIUpdates')?.value;
    this.emailPreferenceForm.get('TechnicalOperationalAlerts')?.setValue(isChecked);
  }

  async updateEmailPreference() {
    this.submitted = true;
    const token = this.entraAuthService.getTokenObject();

    if (!token || !this.emailPreferenceForm.valid) return;

    this.userAccountRequest = this.buildUserAccountRequest();
    const queryParams = new HttpParams().append('id', this.userId);
    this.apiResponse = await this.apiService.sendPostRequestWithParams(this.userAccountRequest, '/user', queryParams);
    this.handleUpdateResponse();
  }

  private buildUserAccountRequest(): UserAccount {
    const formValue = this.emailPreferenceForm.value;
    return {
      identity: {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: this.user.userEmail,
      },
      emailPreference: {
        commercialNews: formValue.NDCCommercialNews ? 'Yes' : 'No',
        iagPortals: formValue.IAGPortalUsers ? 'Yes' : 'No',
        platformUpdates: formValue.platformUpdates ? 'Yes' : 'No',
        operationalNewAPIUpdates: formValue.operationalNewAPIUpdates ? 'Yes' : 'No',
        operationalOldAPIUpdates: formValue.operationalOldAPIUpdates ? 'Yes' : 'No'
      }
    };
  }

  private handleUpdateResponse(): void {
    if (this.apiResponse.error) {
      this.handleUpdateError();
    } else {
      this.showSuccessMessage();
    }
  }

  private handleUpdateError(): void {
    if (this.apiResponse.error.error) {
      this.registrationError = this.apiResponse.error.message || this.apiResponse.error.error;
    }
    if (this.apiResponse.error.status === 401) {
      this.redirectToLogin();
    }
    setTimeout(() => (this.registrationError = ''), 3000);
  }

  private showSuccessMessage(): void {
    this.showSaveSuccessMsg = true;
    setTimeout(() => (this.showSaveSuccessMsg = false), 3000);
  }

  private redirectToLogin(): void {
    localStorage.setItem('redirect_url', '/profile');
    this.entraAuthService.initLogin();
  }

  async getUserInformation(userId: string) {
    const queryParams = new HttpParams().append('id', userId);
    this.apiResponse = await this.apiService.sendGetRequest('/user', queryParams);

    if (this.apiResponse.success) {
      this.updateUserModel();
      this.mapBannerWelcomeName(this.apiResponse.success);
      this.mapMailPreferenceToggle(this.apiResponse.success);
    } else if (this.apiResponse.error) {
      this.handleGetUserError();
    }
  }

  private updateUserModel(): void {
    const response = this.apiResponse.success;
    if (response.firstName != null && response.firstName !== 'null') {
      this.user.userEmail = response.email;
      this.user.userId = response.userId;
      this.user.mobileNumber = response.mobileNumber;
      this.user.isAdminUser = response.groupName?.includes('SiteAdmin') || false;
      this.user.isNDCAPINewSandboxUser = true;
      this.user.isSMEUser = response.groupName?.includes('NDCSandBoxLiveRO') || false;
      this.sandboxPageStateService.updateUser(this.user);
    } else {
      this.router.navigate(['/account/register']);
    }
  }

  private handleGetUserError(): void {
    if (this.apiResponse.error.error) {
      this.registrationError = this.apiResponse.error.error;
    }
    if (this.apiResponse.error.error || this.apiResponse.error.status === 401) {
      this.redirectToLogin();
    }
  }

  private mapBannerWelcomeName(userDetails: any): void {
    this.bannerWelcomeName = userDetails?.firstName;
  }

  mapMailPreferenceToggle(userDetails: any): void {
    this.showPreferences = true;
    const emailPreference = userDetails?.emailPreference || {};
    this.emailPreferenceForm.get('NDCCommercialNews')?.setValue(emailPreference.commercialNews?.toLowerCase() === 'yes');
    this.emailPreferenceForm.get('IAGPortalUsers')?.setValue(emailPreference.iagPortals?.toLowerCase() === 'yes');
    this.emailPreferenceForm.get('platformUpdates')?.setValue(emailPreference.platformUpdates?.toLowerCase() === 'yes');
    this.emailPreferenceForm.get('operationalOldAPIUpdates')?.setValue(emailPreference.operationalOldAPIUpdates?.toLowerCase() === 'yes');
    this.emailPreferenceForm.get('operationalNewAPIUpdates')?.setValue(emailPreference.operationalNewAPIUpdates?.toLowerCase() === 'yes');
    const techAlerts = emailPreference.operationalOldAPIUpdates?.toLowerCase() === 'yes' && emailPreference.operationalNewAPIUpdates?.toLowerCase() === 'yes';
    this.emailPreferenceForm.get('TechnicalOperationalAlerts')?.setValue(techAlerts);
    this.emailPreferenceForm.get('firstName')?.setValue(userDetails?.firstName || '');
    this.emailPreferenceForm.get('lastName')?.setValue(userDetails?.lastName || '');
  }

  get f(): { [key: string]: AbstractControl } {
    return this.emailPreferenceForm.controls;
  }
}
